import MergeTypeRounded from "@mui/icons-material/MergeTypeRounded";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { DUPLICATE_PAIR } from "../../../theme/tokens";
import type { NurseMergeResult } from "../../../utils/nurseMergeModels";
import { MergeDuplicateDialog } from "../../Profile/v2/MergeDuplicate/MergeDuplicateDialog";
import type { NurseResult } from "../hooks";
import { buildFloatPoolMatchColumns } from "./floatPoolMatchColumns";
import { type FloatPoolMergeRow, floatPoolMergeRows, mergeOutcomes, type RowMergeOutcome } from "./floatPoolMerge";

type Props = {
	results: NurseResult[];
	dataTestId: string;
};

const rowId = (row: FloatPoolMergeRow): string => row.key;

/**
 * Roster upload rows whose person already has a second staff record — the same email or phone on a record
 * elsewhere in the organization, or two same-name records here — each with a way to merge the pair. Renders
 * nothing when the upload found none. These results live only in this page's memory, so the hint points
 * at Team → Duplicate employees for coming back later.
 */
export const FloatPoolMatches: FC<Props> = ({ results, dataTestId }) => {
	const { t } = useTranslation();
	const [reviewing, setReviewing] = useState<FloatPoolMergeRow | null>(null);
	const [outcomes, setOutcomes] = useState<ReadonlyMap<string, RowMergeOutcome>>(() => new Map());
	const [announcement, setAnnouncement] = useState("");
	const sectionRef = useRef<HTMLDivElement>(null);
	const lastNameFirst = useLastNameFirst();
	const rows = useMemo(() => floatPoolMergeRows(results, lastNameFirst), [results, lastNameFirst]);
	const columns = useMemo(
		() => buildFloatPoolMatchColumns(t, { dataTestId, outcomes, onReview: setReviewing, lastNameFirst }),
		[t, dataTestId, outcomes, lastNameFirst],
	);

	// The button that opened the dialog is replaced by a chip once the merge lands, so the dialog has nowhere to
	// return focus to. Land it on this section instead of letting it fall to the page body.
	useEffect(() => {
		if (announcement) sectionRef.current?.focus();
	}, [announcement]);

	if (rows.length === 0) return null;

	const settle = (result: NurseMergeResult) => {
		setOutcomes((current) => {
			const next = new Map(current);
			// A row already merged stays merged: a later merge of its surviving record doesn't undo that.
			for (const [key, outcome] of mergeOutcomes(rows, result)) if (current.get(key)?.kind !== "merged") next.set(key, outcome);
			return next;
		});
		setAnnouncement(t("onboarding_load.float_pool.merged_announcement", { mergedId: result.mergedNurseId, keptId: result.primaryNurseId }));
		setReviewing(null);
	};

	return (
		<Box
			ref={sectionRef}
			// Programmatic focus target only (see the effect above); it is never in the tab order.
			tabIndex={-1}
			sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1, outline: "none" }}
			data-testid={`${dataTestId}-float-pool`}
		>
			<PanelHeader
				icon={<MergeTypeRounded />}
				title={t("onboarding_load.float_pool.title", { count: rows.length })}
				dataTestId={`${dataTestId}-float-pool-header`}
			/>
			<InlineHint dataTestId={`${dataTestId}-float-pool-hint`}>{t("onboarding_load.float_pool.hint")}</InlineHint>
			<SystemDataGrid<FloatPoolMergeRow>
				rows={rows}
				columns={columns}
				getRowId={rowId}
				containedHeight
				dataGridProps={{
					"aria-label": t("onboarding_load.float_pool.grid_aria"),
					// Standard, not compact: the record cells are three lines tall, as on Team → Duplicate employees.
					density: "standard",
					rowHeight: DUPLICATE_PAIR.ROW_HEIGHT,
				}}
				dataTestId={`${dataTestId}-float-pool-grid`}
			/>
			<Box role="status" aria-live="polite" sx={visuallyHidden} data-testid={`${dataTestId}-float-pool-status`}>
				{announcement}
			</Box>
			{reviewing && (
				<MergeDuplicateDialog
					nurse={reviewing.pair.nurse}
					pairedNurse={reviewing.pair.pairedNurse}
					entryPoint="onboarding_roster_upload"
					onClose={() => setReviewing(null)}
					onMerged={settle}
				/>
			)}
		</Box>
	);
};
